import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LegalDisclaimer from '../components/LegalDisclaimer';
import { PRO_ACCESS_URL, PRO_BUNDLE_NAME } from '../config/proFeatures';

const Settings = () => {
  const { user, isPro, loading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle>Sign in to view account settings</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Please sign in to manage your Neon Alpha account and subscription.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
          <Badge className={isPro ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'}>
            {isPro ? 'Pro' : 'Free'} Plan
          </Badge>
        </div>

        {/* Profile card */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Email</span>
              <span className="font-medium text-gray-900">{user.email}</span>
            </div>
            {user.name && (
              <div className="flex justify-between">
                <span className="text-gray-600">Name</span>
                <span className="font-medium text-gray-900">{user.name}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Membership card */}
        <Card>
          <CardHeader>
            <CardTitle>Membership</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm" data-testid="settings-membership-card">
            {isPro ? (
              <>
                <p className="text-gray-600">
                  You have <span className="font-semibold">Pro Access</span> — every feature on the
                  platform is unlocked, including unlimited searches, journaling, research, and the
                  private community.
                </p>
                <p className="text-xs text-gray-500">
                  Pro access is included with your {PRO_BUNDLE_NAME} purchase. For any questions
                  about your membership, reach out via the contact options on our Linktree.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600">
                  You are on the <span className="font-semibold">Free</span> plan. Pro access is
                  included with <span className="font-semibold">{PRO_BUNDLE_NAME}</span> — a digital
                  product bundle of wealth information and trading-app membership.
                </p>
                <Button
                  className="bg-purple-600 hover:bg-purple-700 text-white text-sm"
                  onClick={() => window.open(PRO_ACCESS_URL, '_blank', 'noopener')}
                  data-testid="settings-get-pro-btn"
                >
                  Get {PRO_BUNDLE_NAME}
                  <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                </Button>
                <p className="text-[11px] text-gray-400">
                  Purchase with this account&apos;s email ({user.email}). Your account is manually
                  upgraded to Pro within 24 hours of purchase.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Legal Disclaimer */}
        <LegalDisclaimer variant="compact" />
      </div>
    </div>
  );
};

export default Settings;
